/**
 * useConnectedIntegrations — ONE derivation of "which registry integration ids
 * are connected in this workspace", shared by the Integrations marketplace and
 * every surface that keys behaviour off connections (funnel live/paused pills,
 * the setup checklist's self-ticking steps).
 *
 * Extracted verbatim from IntegrationsMarketplace's fetchConnected (design
 * handoff kind-alignment §2) so the two can never drift: DB Integration rows
 * map through PLATFORM_TO_REGISTRY_IDS (platform/type → ids, incl. the Meta
 * trio), exact-id name match, name-prefix match, plus the Claude key status.
 *
 * SOCIAL ACCOUNTS ARE A SECOND SOURCE, AND IT IS OPT-IN. A channel connected
 * through /api/social/oauth (LinkedIn, Pinterest, YouTube, X) writes a
 * `SocialAccount` row, not an `Integration` row — so reading only
 * /integrations left those cards showing "Connect" forever, no matter how many
 * times the merchant connected. The rows go through the SAME derivation:
 * `platform` lowercases onto the same PLATFORM_TO_REGISTRY_IDS keys ('YOUTUBE'
 * → youtube, 'X' → x_social), so this is one more input, not a second matcher.
 *
 * It is off by default because the second table costs a second request, and
 * the Business Hub — which mounts this hook through PipelinesCard and the
 * setup checklist — has a counted initial-load budget (hubCallBudget.test.tsx)
 * that no surface should grow for an answer it does not draw. The hub keys off
 * store, email and messaging connections; only the Integrations marketplace
 * renders a social channel's tile, so only it asks.
 */
import { useCallback, useEffect, useState } from 'react';
import { ASSET_GATED_REGISTRY_IDS, INTEGRATIONS, NEEDS_REAUTH_STATUS, PLATFORM_TO_REGISTRY_IDS, SCOPE_GATED_REGISTRY_IDS, isLiveIntegrationRow } from '../config/integrations';
import { integrationsApi } from '../services/api';
import { apiClient } from '../services/apiClient';
import type { CapabilityRow } from '../types/capabilityMatrix';

export interface ConnectedIntegrationsOptions {
  /**
   * Also read /social/accounts, for surfaces that render social channel tiles.
   * Costs one extra request — see the note above.
   */
  includeSocialAccounts?: boolean;
  /**
   * Also read /ads/providers, for surfaces that claim an ad account is linked.
   *
   * An ad grant does NOT live in the Integration table — it is an AdCredential,
   * written only when the OAuth grant actually carried `ads_read`. So no amount
   * of reading /integrations can answer "is an ad account connected?", and the
   * registry map used to paper over that by having a Facebook PAGE row imply
   * `meta_ads`. It doesn't any more, which means a surface that needs the
   * answer has to ask for it.
   *
   * Opt-in for the same reason as the social roster: an extra request, and the
   * Business Hub's initial load has a counted budget (hubCallBudget.test.tsx)
   * that no surface should grow for an answer it does not draw.
   */
  includeAdAccounts?: boolean;
  /**
   * Also read /integrations/verify — the stored capability matrix — and demote
   * a platform whose authentication check FAILED from connected to attention.
   *
   * A row proves a connection was made; the matrix proves it still works. A
   * platform with no check yet stays as its row says (checks run right after
   * every connect, and on "Verify now"), so this only ever takes a badge away
   * on evidence, never on the absence of it. One extra request, opt-in for the
   * same call-budget reason as the two above.
   */
  includeVerification?: boolean;
}

export interface ConnectedIntegrationsState {
  /** Registry ids (plus virtual ids like whatsapp_business) currently connected. */
  connectedIds: Set<string>;
  /**
   * Registry ids whose row is `needs_reauth` — the credential died and only a
   * reconnect revives it. Derived from the SAME /integrations response, so it
   * costs no extra request (the Business Hub's call budget stays intact).
   */
  attentionIds: Set<string>;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * The shape every caller can satisfy: raw `Record<string, unknown>` rows from
 * a bare fetch, and typed row interfaces alike. Widened from
 * `Array<Record<string, unknown>>` so typed callers (AcquisitionCard's
 * ConnectedIntegration) need no cast to reuse this derivation instead of
 * growing a second, weaker matcher.
 */
export interface IntegrationRowLike {
  platform?: unknown;
  type?: unknown;
  name?: unknown;
  status?: unknown;
  /** What the provider said this connection may do. See scopeGatedIds. */
  grantedScopes?: unknown;
  /** The WhatsApp number this workspace can send from, if one was attached. */
  whatsappPhoneNumberId?: unknown;
}

/** Non-strings (null platform, absent name) read as absent, never as "null". */
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Ids this row may claim because the PROVIDER granted the permission.
 *
 * `meta_ads` used to ride along with any Facebook row, so connecting Messenger
 * lit up "Meta Ads · Connected" and ticked the setup checklist's "Link your ad
 * account" while `AdCredential` held zero rows. Meta grants permissions one at
 * a time; a row without an ads permission cannot run ads however green its
 * badge is.
 *
 * An absent or empty scope list claims NOTHING. It means "we have not asked" —
 * true of every connection made before scopes were recorded — and inventing an
 * answer in either direction is what this replaces.
 */
function scopeGatedIds(row: IntegrationRowLike, platform: string): string[] {
  const scopes = Array.isArray(row.grantedScopes)
    ? (row.grantedScopes as unknown[]).filter((s): s is string => typeof s === 'string')
    : [];
  if (scopes.length === 0) return [];
  return Object.entries(SCOPE_GATED_REGISTRY_IDS)
    .filter(([, rule]) => rule.platforms.includes(platform) && rule.anyScope.some((s) => scopes.includes(s)))
    .map(([id]) => id);
}

/**
 * Ids this row may claim because the asset it needs is ATTACHED.
 *
 * Unlike scopeGatedIds, an absent field here is a definite no rather than an
 * unanswered question: `whatsappPhoneNumberId` is written by our own embedded
 * signup, so a null one records a step that never ran. Production's single
 * WHATSAPP row has been `active` since 2026-06-14 with no number and has never
 * carried a message; it lit the channel, ticked its setup step and counted
 * toward the funnel pills regardless.
 */
function assetGatedIds(row: IntegrationRowLike, platform: string): string[] {
  const fields = row as unknown as Record<string, unknown>;
  return Object.entries(ASSET_GATED_REGISTRY_IDS)
    .filter(
      ([, rule]) =>
        rule.platforms.includes(platform) && asString(fields[rule.requiresField]).trim() !== '',
    )
    .map(([id]) => id);
}

/**
 * Derive registry ids from raw /integrations rows — pure, unit-testable.
 *
 * Lowercasing the platform is what makes this work for BOTH storage
 * conventions at once: the Meta family's UPPERCASE 'FACEBOOK'/'INSTAGRAM'/
 * 'WHATSAPP' and 'GOOGLE_ADS' normalise onto the same keys as the lowercase
 * family. This function does NOT judge status — compose it with
 * `isLiveIntegrationRow` when the caller means "connected AND usable".
 */
export function deriveConnectedIds(rows: ReadonlyArray<IntegrationRowLike>): Set<string> {
  const ids = new Set<string>();
  for (const conn of rows) {
    const platform = (asString(conn.platform) || asString(conn.type)).toLowerCase();
    const mapped = PLATFORM_TO_REGISTRY_IDS[platform];
    if (mapped) {
      mapped.forEach((id) => ids.add(id));
    }
    scopeGatedIds(conn, platform).forEach((id) => ids.add(id));
    assetGatedIds(conn, platform).forEach((id) => ids.add(id));
    // Also try exact match on integration.id or name prefix
    const name = asString(conn.name).toLowerCase().replace(/[\s-]+/g, '_');
    if (INTEGRATIONS.some((i) => i.id === name)) {
      ids.add(name);
    }
    // Match by name prefix (e.g. "Gmail (email)" → "gmail"). THIS IS A SECOND
    // WAY IN, and it is why `whatsapp` had to leave PLATFORM_TO_REGISTRY_IDS
    // rather than merely lose its id: production's row is named "whatsapp
    // Integration", so the prefix alone would have re-claimed the channel with
    // the platform mapping already corrected. Both gates run on the prefix too,
    // so a name can no longer buy more than the platform it names.
    const namePrefix = asString(conn.name).toLowerCase().split(/[\s(]/)[0];
    const prefixMapped = PLATFORM_TO_REGISTRY_IDS[namePrefix];
    if (prefixMapped) {
      prefixMapped.forEach((id) => ids.add(id));
    }
    if (namePrefix && namePrefix !== platform) {
      scopeGatedIds(conn, namePrefix).forEach((id) => ids.add(id));
      assetGatedIds(conn, namePrefix).forEach((id) => ids.add(id));
    }
  }
  return ids;
}

/** The rows that need the merchant, mapped onto the same registry ids. */
export function deriveAttentionIds(rows: ReadonlyArray<IntegrationRowLike>): Set<string> {
  const needing = rows.filter(
    (row) => typeof row.status === 'string' && row.status.trim().toLowerCase() === NEEDS_REAUTH_STATUS,
  );
  return deriveConnectedIds(needing);
}

/**
 * Matrix platform keys that PLATFORM_TO_REGISTRY_IDS deliberately does not map
 * (see its comment on `whatsapp`): the matrix key is the connection itself, so
 * the asset gate that keeps a number-less WhatsApp row dark does not apply.
 */
const VERIFY_PLATFORM_EXTRA_IDS: Readonly<Record<string, readonly string[]>> = {
  whatsapp: ['whatsapp_business'],
  email: ['email_sending'],
};

/**
 * Registry ids whose platform has an `auth` check on record and NOT ONE of
 * them passed. Pure, unit-testable. A platform with two pages of which one
 * still authenticates keeps its id — the live connection backs it.
 */
export function deriveUnverifiedIds(rows: ReadonlyArray<Pick<CapabilityRow, 'platform' | 'capability' | 'status'>>): Set<string> {
  const byPlatform = new Map<string, { any: boolean; ok: boolean }>();
  for (const row of rows) {
    if (row.capability !== 'auth') continue;
    const key = row.platform.toLowerCase();
    const cur = byPlatform.get(key) ?? { any: false, ok: false };
    cur.any = true;
    if (row.status === 'ok') cur.ok = true;
    byPlatform.set(key, cur);
  }
  const ids = new Set<string>();
  for (const [platform, seen] of byPlatform) {
    if (!seen.any || seen.ok) continue;
    (PLATFORM_TO_REGISTRY_IDS[platform] ?? []).forEach((id) => ids.add(id));
    (VERIFY_PLATFORM_EXTRA_IDS[platform] ?? []).forEach((id) => ids.add(id));
  }
  return ids;
}

export function useConnectedIntegrations(
  options: ConnectedIntegrationsOptions = {},
): ConnectedIntegrationsState {
  const includeSocial = options.includeSocialAccounts === true;
  const includeAds = options.includeAdAccounts === true;
  const includeVerification = options.includeVerification === true;
  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());
  const [attentionIds, setAttentionIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await integrationsApi.list();
      const rawData = Array.isArray(response)
        ? response
        : (response as unknown as { data?: unknown[] })?.data || [];
      /**
       * LIVE rows only. `deriveConnectedIds` deliberately does not judge
       * status — its own doc says to compose it with `isLiveIntegrationRow`
       * when the caller means "connected AND usable" — and this caller, which
       * paints the green Connected badge, means exactly that.
       *
       * Passing every row put a `needs_reauth` integration into BOTH sets, and
       * the "connected wins" rule below then deleted it from attention. A
       * workspace whose only Facebook row had a dead token rendered a plain
       * green "Connected" with no way to discover otherwise. Six integrations
       * were in that state on 2026-09-15 and every one of them read as fine.
       */
      const rows = rawData as Array<Record<string, unknown>>;
      const ids = deriveConnectedIds(rows.filter((row) => isLiveIntegrationRow(row)));
      const attention = deriveAttentionIds(rows);

      // Social channels connected via OAuth live in SocialAccount. A failure
      // here must not blank the Integration-derived ids, so it is caught
      // separately and simply contributes nothing.
      if (includeSocial) {
        try {
          const social = await apiClient.get<{ data?: unknown }>('/social/accounts');
          const rows = Array.isArray(social.data) ? social.data : [];
          deriveConnectedIds(rows as Array<Record<string, unknown>>).forEach((id) => ids.add(id));
        } catch {
          // Same reasoning as the Claude probe below.
        }
      }

      // Ad accounts live in AdCredential, not Integration — the only truthful
      // source is the ads API. Same failure policy as the probes around it: a
      // failure contributes nothing rather than blanking the derivation.
      if (includeAds) {
        try {
          const ads = await apiClient.get<{ data?: { providers?: Array<{ slug?: unknown; isConnected?: unknown; accountCount?: unknown }> } }>(
            '/ads/providers'
          );
          for (const provider of ads.data?.providers ?? []) {
            // `isConnected` alone means a live credential; an ad account must
            // also exist, or there is nothing to spend against. The slugs are
            // already the registry ids (google_ads, meta_ads).
            const usable =
              provider.isConnected === true && typeof provider.accountCount === 'number' && provider.accountCount > 0;
            if (usable && typeof provider.slug === 'string') ids.add(provider.slug);
          }
        } catch {
          // Same reasoning as the Claude probe below.
        }
      }

      // Claude (MCP) — connected if any active Claude API key exists
      try {
        const claude = await apiClient.get<{ success: boolean; data: { connected: boolean } }>(
          '/integrations/claude/status'
        );
        if (claude.data?.connected) ids.add('claude');
      } catch {
        // Endpoint failure shouldn't block the rest of the derivation
      }

      // A registry id can be fed by two rows — one Meta connect feeds three —
      // so an id backed by a LIVE row stays green even when a second, dead row
      // maps to it too, and is never green and amber at once. `ids` now holds
      // only live-backed ids, which is what makes this subtraction mean "a
      // healthy row provides this" instead of the old "some row mentions it".
      ids.forEach((id) => attention.delete(id));

      // The capability matrix is the last word: a platform whose stored
      // authentication check failed is not connected, whatever its row says.
      // Applied AFTER the subtraction above so it cannot be undone by it.
      if (includeVerification) {
        try {
          const matrix = await integrationsApi.verifyMatrix();
          const rows = Array.isArray(matrix.data?.rows) ? matrix.data.rows : [];
          deriveUnverifiedIds(rows).forEach((id) => {
            if (ids.delete(id)) attention.add(id);
          });
        } catch {
          // Same failure policy as the probes above: no answer takes nothing away.
        }
      }
      setConnectedIds(ids);
      setAttentionIds(attention);
    } catch {
      setConnectedIds(new Set());
      setAttentionIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [includeSocial, includeAds, includeVerification]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { connectedIds, attentionIds, loading, refresh };
}
